import { useState } from 'react';
import { Layout } from '@/components/layout';
import { Avatar, Card, Input, Textarea, Button } from '@/components/atoms';
import { useAuthStore } from '@/store';
import { useBackButton, useMainButton } from '@/hooks';
import { specialists } from '@/api';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import { resolveMediaUrl } from '@/utils/media';
import styles from './pages.module.css';

export function MyProfileEditPage(): JSX.Element {
  useBackButton();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const sp = user?.specialist_profile;
  const [title, setTitle] = useState(sp?.title ?? '');
  const [bio, setBio] = useState(sp?.bio ?? '');
  const [radius, setRadius] = useState(sp?.service_radius_km ?? 10);
  const [cancelHours, setCancelHours] = useState(sp?.cancellation_range_hours ?? 24);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(sp?.avatar_url ?? null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    // Optimistic preview so the user doesn't stare at the old image while
    // the upload + Pillow re-encode happen on the server.
    const localUrl = URL.createObjectURL(file);
    setAvatarUrl(localUrl);
    try {
      const updated = await specialists.uploadAvatar(file);
      setAvatarUrl(updated.avatar_url ?? null);
      if (user) {
        setUser({
          ...user,
          specialist_profile: { ...(user.specialist_profile ?? updated), ...updated },
        });
      }
      toast.success('Аватар оновлено');
    } catch (err) {
      logger.error('Avatar upload failed', err);
      toast.error('Не вдалось завантажити аватар');
      setAvatarUrl(sp?.avatar_url ?? null);
    } finally {
      setAvatarBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    try {
      await specialists.updateMySpecialist({
        title: title.trim(),
        bio,
        service_radius_km: radius,
        cancellation_range_hours: cancelHours,
      });
      toast.success('Збережено');
    } catch (err) {
      logger.error('Save profile failed', err);
      toast.error('Не вдалось зберегти');
    }
  };

  useMainButton({ text: 'Зберегти', onClick: save });

  if (!sp) {
    return (
      <Layout title="Редагувати">
        <Card>
          <p>Спочатку треба стати спеціалістом — повернись на /me і натисни «Заявка».</p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Редагувати профіль">
      <div className={styles.formStack}>
        {/* F-180 (B9): avatar upload. The field was missing — specialists
            could not change their avatar from the cabinet at all. */}
        <Card>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {avatarUrl ? (
              <img
                src={resolveMediaUrl(avatarUrl)}
                alt="Аватар"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Avatar name={user?.first_name ?? sp.slug} size={72} />
            )}
            <div style={{ flex: 1 }}>
              <label
                htmlFor="avatar-upload"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--fg-muted)',
                  marginBottom: 4,
                }}
              >
                Аватар
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={(e) => void handleAvatarChange(e)}
                disabled={avatarBusy}
              />
              <p className={styles.note}>
                JPEG / PNG / WebP, до 5 МБ. Бекенд перекодує і ріжне до квадрата.
              </p>
            </div>
          </div>
        </Card>
        <Card>
          {/* F-170 B3.1: short "what you do" headline shown in the
              profile hero next to the name and as a fallback subline on
              the SpecialistCard. 80 chars to keep it tweet-like. */}
          <Input
            label="Хто ти за фахом"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            placeholder="Сімейний психолог, Весільний організатор..."
          />
        </Card>
        <Card>
          <Textarea
            label="Біо"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={5}
            placeholder="Розкажи коротко про себе і досвід."
          />
        </Card>
        <Card>
          <div className={styles.formRow}>
            <Input
              label="Радіус виїзду (км)"
              type="number"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              min={0}
              max={500}
            />
            <Input
              label="Ренж комфорту (год)"
              type="number"
              value={cancelHours}
              onChange={(e) => setCancelHours(Number(e.target.value))}
              min={0}
              max={168}
            />
          </div>
          <p className={styles.note}>
            Якщо клієнт скасовує менше ніж за {cancelHours} год до часу — це поза твоїм ренжем.
          </p>
        </Card>
        <Button onClick={save} fullWidth>
          Зберегти
        </Button>
      </div>
    </Layout>
  );
}
